if (!customElements.get("page-password-gate")) {
  customElements.define(
    "page-password-gate",
    class PagePasswordGate extends HTMLElement {
      constructor() {
        super();
        this.storageKeyPrefix = "foxtheme:page-password:";
        this.password = "";
        this.pageHandle = "";
      }

      connectedCallback() {
        this.password = this.dataset.password || "";
        this.pageHandle = this.dataset.pageHandle || "default";
        this.storageKey = this.storageKeyPrefix + this.pageHandle;

        // Always show in design mode for configuration
        if (Shopify.designMode) {
          this.showInDesignMode();
          return;
        }

        // Don't show gate if no password is set
        if (!this.password) {
          this.hideGate();
          this.showProtectedContent();
          return;
        }

        // Check if password is already validated for this page
        if (this.isPasswordValidated()) {
          this.hideGate();
          this.showProtectedContent();
          return;
        }

        // Show the gate and hide protected content
        this.showGate();
        this.hideProtectedContent();
        this.bindEvents();
      }

      waitForThemeUtils(callback) {
        if (window.FoxTheme && window.FoxTheme.utils) {
          callback();
        } else {
          // Wait for theme utilities to load
          setTimeout(() => this.waitForThemeUtils(callback), 100);
        }
      }

      showInDesignMode() {
        // In design mode, show the gate but don't hide content
        this.style.display = "block";
        this.classList.add("page-password-gate--design-mode");
        this.showProtectedContent(); // Ensure all content is visible in design mode
        this.bindEvents();
      }

      isPasswordValidated() {
        // Always bypass in design mode
        if (Shopify.designMode) return false;

        // Check if utilities are available, if not, use fallback localStorage
        if (!window.FoxTheme || !window.FoxTheme.utils) {
          try {
            const saved = localStorage.getItem(this.storageKey);
            if (saved) {
              const data = JSON.parse(saved);
              // Check if not expired
              if (data.expires && Date.now() < data.expires) {
                return data.unlocked === true;
              }
            }
          } catch (e) {
            // Ignore localStorage errors
          }
          return false;
        }
        const savedData = window.FoxTheme.utils.getStorage(this.storageKey);
        return savedData && savedData.unlocked === true;
      }

      hideProtectedContent() {
        // Find all sections that should be hidden when password gate is active
        const protectedSections = document.querySelectorAll(
          ".shopify-section:not([data-password-gate])"
        );

        protectedSections.forEach((section) => {
          // Skip header, footer, and announcement sections
          if (this.isAlwaysVisibleSection(section)) {
            return;
          }

          section.style.display = "none";
          section.setAttribute("data-password-hidden", "true");
        });
      }

      showProtectedContent() {
        // Show all hidden content
        const hiddenElements = document.querySelectorAll(
          '[data-password-hidden="true"]'
        );

        hiddenElements.forEach((element) => {
          element.style.display = "";
          element.removeAttribute("data-password-hidden");
        });
      }

      isAlwaysVisibleSection(section) {
        // Check if this is a section that should always be visible
        const alwaysVisibleTypes = [
          "header",
          "footer",
          "announcement-bar",
          "newsletter-popup",
          "cookie-banner",
          "page-password-gate",
        ];

        const sectionId = section.id || "";
        const sectionClasses = section.className || "";

        // Check section ID patterns
        for (const type of alwaysVisibleTypes) {
          if (sectionId.includes(type) || sectionClasses.includes(type)) {
            return true;
          }
        }

        // Check if this is the password gate section itself
        if (section.querySelector("page-password-gate")) {
          return true;
        }
        return false;
      }

      showGate() {
        // Make sure the gate section is visible
        this.style.display = "block";
        this.setAttribute("aria-hidden", "false");
        this.classList.add("page-password-gate--active");

        // Focus on password input
        const passwordInput = this.querySelector('input[type="password"]');
        if (passwordInput) {
          setTimeout(() => passwordInput.focus(), 100);
        }
      }

      hideGate() {
        this.style.display = "none";
        this.setAttribute("aria-hidden", "true");
        this.classList.remove("page-password-gate--active");
      }

      unlockPage() {
        this.hideGate();
        this.showProtectedContent();
      }

      savePasswordValidation() {
        // Don't save in design mode
        if (Shopify.designMode) return;

        // Check if utilities are available - if not, use fallback localStorage
        if (!window.FoxTheme || !window.FoxTheme.utils) {
          try {
            const expirationTime = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
            localStorage.setItem(
              this.storageKey,
              JSON.stringify({
                unlocked: true,
                timestamp: Date.now(),
                expires: expirationTime,
              })
            );
          } catch (e) {
            console.warn("Could not save password validation to localStorage");
          }
          return;
        }

        // Save validation for 24 hours using theme utilities
        window.FoxTheme.utils.setStorage(
          this.storageKey,
          { unlocked: true, timestamp: Date.now() },
          1 // 1 day
        );
      }

      showError() {
        const errorElement = this.querySelector("[data-error-message]");
        if (errorElement) {
          errorElement.removeAttribute("hidden");
          setTimeout(() => {
            errorElement.setAttribute("hidden", "");
          }, 5000);
        }
      }

      validatePassword(inputPassword) {
        return inputPassword === this.password;
      }

      bindEvents() {
        const form = this.querySelector("[data-password-form]");
        const passwordInput = this.querySelector('input[type="password"]');

        if (form) {
          form.addEventListener("submit", (e) => {
            e.preventDefault();
            this.handleFormSubmit(e);
          });
        }

        if (passwordInput) {
          // Clear error when typing
          passwordInput.addEventListener("input", () => {
            const errorElement = this.querySelector("[data-error-message]");
            if (errorElement) {
              errorElement.setAttribute("hidden", "");
            }
          });
        }
      }

      handleFormSubmit(e) {
        const formData = new FormData(e.target);
        const inputPassword = formData.get("password");

        if (this.validatePassword(inputPassword)) {
          // Password correct
          this.savePasswordValidation();
          this.unlockPage();
        } else {
          // Password incorrect
          this.showError();
          const passwordInput = this.querySelector('input[type="password"]');
          if (passwordInput) {
            passwordInput.value = "";
            passwordInput.focus();
          }
        }
      }
    }
  );
}
